import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { themeOf, tint } from "@/lib/mall";

const field = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none";

/** Message a shop — lands in that shop's inbox with an email ping. Vendors get stamped as a shop. */
export default function MessageShop({ store }) {
    const { user } = useAuth();
    const t = themeOf(store);
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const send = async (e) => {
        e?.preventDefault();
        if (!form.name || !form.email || !form.message) { toast.error("Name, email and a message please"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/shop-inbox/${store.slug}/message`, form);
            setDone(true);
            toast.success(`${data.to_shop} has it — they reply straight to your email`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (done) return (
        <div className="mt-14 rounded-3xl border p-7" style={{ borderColor: tint(t.primary, 0.4), background: tint(t.primary, 0.06) }}
            data-testid="message-shop-done">
            <h2 className="font-display text-xl font-black tracking-tight">Message delivered.</h2>
            <p className="mt-1 text-sm text-zinc-300">
                It's in {store.name}'s inbox and their email. You got a copy too — replies come straight to you.
            </p>
        </div>
    );

    return (
        <form onSubmit={send} className="mt-14 rounded-3xl border p-7" data-testid="message-shop"
            style={{ borderColor: tint(t.primary, 0.35), background: tint(t.primary, 0.04) }}>
            <h2 className="font-display text-xl font-black tracking-tight">Message {store.name}</h2>
            <p className="mt-1 text-sm text-zinc-400">
                Goes straight to this shop's own inbox — not 13thav support. Ask about a job, a price or a trade.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <input className={field} placeholder="Your name" value={form.name} onChange={set("name")} data-testid="message-name" />
                <input className={field} placeholder="Your email" type="email" value={form.email} onChange={set("email")} data-testid="message-email" />
                <input className={field} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="message-phone" />
            </div>
            <input className={`${field} mt-3`} placeholder="What's it about?" value={form.subject} onChange={set("subject")} data-testid="message-subject" />
            <textarea className={`${field} mt-3`} rows={4} placeholder="Your message…" value={form.message} onChange={set("message")} data-testid="message-body" />
            <button type="submit" disabled={busy} data-testid="message-send"
                className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide text-black transition-transform hover:scale-[1.03] disabled:opacity-50"
                style={{ background: t.primary }}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send it
            </button>
        </form>
    );
}
